import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Chip, 
  IconButton, 
  Tooltip, 
  Box,
  Typography,
  Stack,
  alpha,
  useTheme
} from '@mui/material';
import {
  Star as StarIcon,
  RateReview as ReviewIcon,
  CheckCircle as CheckedIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { RESERVATION_STATUS } from '../../constants/statusCodes';

/**
 * Composant indépendant pour gestion des évaluations dans la liste des réservations
 * S'intègre facilement dans MyReservationsPage sans modification du code existant
 */
const ReservationReviewButton = ({ 
  reservation, 
  onReviewClick, 
  disabled = false 
}) => {
  const { t } = useTranslation('reviews');
  const theme = useTheme();
  
  const [reviewStatus, setReviewStatus] = useState({
    canReview: false,
    hasReviewed: false,
    loading: true,
    reason: null,
    daysRemaining: null,
    deadlineExpired: false
  });

  // Vérifier le statut de review pour cette réservation
  useEffect(() => {
    const mapEligibilityToState = (eligibility) => {
      if (!eligibility) {
        return null;
      }

      const deadlineExpired = !eligibility.can_review && eligibility.reason === 'Review deadline expired';

      return {
        canReview: eligibility.can_review,
        hasReviewed: eligibility.has_reviewed || false,
        loading: false,
        reason: eligibility.reason,
        daysRemaining: eligibility.details?.days_remaining ?? null,
        deadlineExpired,
        deadlineInfo: eligibility.details || null
      };
    };

    const checkReviewStatus = async () => {
      // Seules les réservations COMPLETED peuvent être évaluées
      if (reservation.status !== RESERVATION_STATUS.COMPLETED) {
        setReviewStatus({
          canReview: false,
          hasReviewed: false,
          loading: false
        });
        return;
      }

      // Utiliser la donnée préchargée si disponible
      if (reservation.reviewEligibility) {
        const mapped = mapEligibilityToState(reservation.reviewEligibility);
        if (mapped) {
          setReviewStatus(mapped);
          return;
        }
      }

      try {
        const response = await api.get(`/reviews/can-review/${reservation.id}`);
        const apiData = response.data.data.data;

        const mapped = mapEligibilityToState(apiData);
        setReviewStatus(mapped || {
          canReview: false,
          hasReviewed: apiData?.has_reviewed || false,
          loading: false,
          reason: apiData?.reason,
          daysRemaining: apiData?.details?.days_remaining ?? null,
          deadlineExpired: !apiData?.can_review && apiData?.reason === 'Review deadline expired',
          deadlineInfo: apiData?.details || null
        });
      } catch (error) {
        console.error('Erreur vérification statut review:', error);
        setReviewStatus({
          canReview: false,
          hasReviewed: false,
          loading: false,
          reason: 'Error checking status',
          daysRemaining: null,
          deadlineExpired: false
        });
      }
    };

    checkReviewStatus();
  }, [reservation.id, reservation.status, reservation.reviewEligibility]);

  // Ne rien afficher si la réservation ne peut pas être évaluée
  if (reviewStatus.loading || reservation.status !== RESERVATION_STATUS.COMPLETED) {
    return null;
  }

  // Si déjà évaluée, vérifier le statut de modération
  if (reviewStatus.hasReviewed) {
    const moderationStatus = reservation.reservationReview?.moderation_status;
    
    // Si rejetée, afficher un chip spécifique non-cliquable
    if (moderationStatus === 'rejected') {
      return (
        <Chip
          icon={<ReviewIcon />}
          label={t('reviewRejected')}
          color="error"
          variant="outlined"
          size="small"
          sx={{
            minHeight: 32,
            '& .MuiChip-label': {
              fontSize: '0.75rem',
              fontWeight: 500
            }
          }}
        />
      );
    }
    
    // Si approuvée ou en attente, afficher le chip de confirmation
    return (
      <Chip
        icon={<CheckedIcon />}
        label={t('alreadyReviewed')}
        color="success"
        variant="outlined"
        size="small"
        sx={{
          minHeight: 32,
          '& .MuiChip-label': {
            fontSize: '0.75rem',
            fontWeight: 500
          }
        }}
      />
    );
  }

  // Si la deadline est expirée, afficher un message d'expiration
  if (reviewStatus.deadlineExpired) {
    const expiredByDays = reviewStatus.deadlineInfo?.expired_by_days;
    return (
      <Tooltip 
        title={
          expiredByDays 
            ? t('deadlineExpiredDetails', { days: expiredByDays })
            : t('deadlineExpiredSimple')
        }
        arrow
      >
        <Chip
          icon={<ReviewIcon />}
          label={t('deadlineExpired')}
          color="error"
          variant="outlined"
          size="small"
          sx={{
            minHeight: 32,
            opacity: 0.7,
            cursor: 'not-allowed',
            '& .MuiChip-label': {
              fontSize: '0.75rem',
              fontWeight: 500
            }
          }}
        />
      </Tooltip>
    );
  }

  // Si peut évaluer, afficher le bouton avec info sur les jours restants
  if (reviewStatus.canReview) {
    const daysRemaining = reviewStatus.daysRemaining;
    const showUrgency = daysRemaining !== null && daysRemaining <= 7;
    
    return (
      <Tooltip
        title={
          daysRemaining !== null
            ? t('daysRemainingTooltip', { days: daysRemaining })
            : t('clickToReview')
        }
        arrow
      >
        <Box sx={{ position: 'relative', width: '100%' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<ReviewIcon />}
            onClick={() => onReviewClick(reservation)}
            disabled={disabled}
            fullWidth
            color="primary"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 32,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              '&:hover': {
                background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              }
            }}
          >
            {t('writeReview')}
          </Button>
          
          {showUrgency && daysRemaining !== null && (
            <Chip
              label={t('daysLeft', { days: daysRemaining })}
              size="small"
              color="warning"
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 700,
                animation: daysRemaining <= 3 ? 'pulse 1.5s infinite' : 'none',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.7 }
                }
              }}
            />
          )}
        </Box>
      </Tooltip>
    );
  }

  return null;
};

export default ReservationReviewButton;